import { Adherence, AdherenceStatus } from "../../model";
import { IParticipant, ProjectCollaboratorRole, WorkspaceCollaboratorRole } from "../../shared/db";
import { Dispatch, SetStateAction, useRef } from "react";
import { useSelectParticipantAdherence, useSelectBatchData, useSelectTheme } from "../../store";
import {
  collaboratorTypeHasRole,
  RIF,
  timeConvert,
  TimeConvertType,
  useCollaboratorTypeInProject,
  useCurrentProjectState,
} from "../../lib";
import { CheckAllIcon, CheckboxDefaultIcon, MessageGreyIcon } from "../../asset/image";
import { Checkbox, NotificationButton, Tag, Tooltip } from "../atoms";
import { shrinkStringInMiddle } from "../utils";
import { BatteryAlert } from "../atoms/battery_alert";

export const AdherenceTableFixedPartEntry = (props: {
  tableHeader?: boolean;
  index?: number;
  participantId?: string;
  adherence?: Adherence;
  displayRealtimeData?: boolean; // realtime Test
  realtimeData?: string; // realtime Test
  selectedList: IParticipant[];
  setSelectedList: Dispatch<SetStateAction<IParticipant[]>>;
  selectableParticipantList: IParticipant[];
  rowHeight: number;
  sendMessage: (participantList: IParticipant[]) => void;
  lastSeenDescending?: boolean;
  setLastSeenDescending?: Dispatch<SetStateAction<boolean>>;
}) => {
  const { color } = useSelectTheme();
  const { projectId, project } = useCurrentProjectState();
  const batchId = project?.batchList?.[0]?.id;
  const participantList: IParticipant[] =
    useSelectBatchData()?.[batchId ?? ""]?.participantList || [];
  const collaboratorType = useCollaboratorTypeInProject();
  const canUpdateParticipant = collaboratorTypeHasRole({
    collaboratorType,
    workspaceRole: WorkspaceCollaboratorRole.ParticipantUpdate,
    projectRole: ProjectCollaboratorRole.ParticipantUpdate,
  });
  const {
    participantId,
    tableHeader,
    index,
    displayRealtimeData,
    realtimeData,
    selectedList,
    setSelectedList,
    selectableParticipantList,
    rowHeight,
    sendMessage,
    lastSeenDescending,
    setLastSeenDescending,
  } = props;

  // TODO: wait BE, should update name from BE
  const participant = participantId
    ? participantList.find((item: IParticipant) => item.id === participantId)
    : undefined;

  const adherence = useSelectParticipantAdherence(projectId, participantId);

  // TODO: wait mobile, last seen time?
  const participantLastSeen = adherence?.status?.unixTimestampLastAppOpen;
  const participantLastSync = adherence?.status?.unixTimestampLastGarminDeviceSync;
  const participantActivated = !!participantLastSeen || !!participantLastSync;

  /* ----------------- select -------------------- */
  const participantSelected: boolean = selectedList.some(
    (item: IParticipant) => item.id === participant?.id,
  );
  const allSelected =
    selectableParticipantList.length > 0 &&
    selectableParticipantList.every((p) => selectedList.some((item) => item.id === p.id));

  const handleClickHeaderSelectBtn = () => {
    if (allSelected) {
      setSelectedList([]);
    } else {
      setSelectedList(selectableParticipantList);
    }
  };

  const handleClickSelect = () => {
    if (!participant) return;
    if (!participantSelected) {
      setSelectedList((prev) => [...prev, participant]);
    } else {
      setSelectedList((prev) => prev.filter((item) => item.id !== participant.id));
    }
  };

  const RowIndex = () => {
    return (
      <div
        css={{
          width: "24px",
          minWidth: "24px",
          display: "flex",
          alignItems: "center",
          color: tableHeader ? color.textIcon.secondary : color.black,
        }}
      >
        {tableHeader ? "#" : (index ?? 0) + 1}
      </div>
    );
  };

  const CheckBox = () => {
    return (
      <div
        css={{
          width: "16px",
          minWidth: "16px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {RIF(
          tableHeader,
          <div
            onClick={handleClickHeaderSelectBtn}
            css={{
              cursor: "pointer",
              width: "16px",
              height: "16px",
            }}
          >
            {RIF(allSelected, <img src={CheckAllIcon} width={16} />)}
            {RIF(!allSelected, <img src={CheckboxDefaultIcon} width={16} />)}
          </div>,
        )}
        {RIF(
          !tableHeader,
          <Checkbox
            name={participant?.insignia}
            onChange={handleClickSelect}
            value={participantSelected}
            data-testid="test_checkbox"
          />,
        )}
      </div>
    );
  };

  const ParticipantInsignia = () => {
    return (
      <div
        css={{
          width: "104px",
          minWidth: "104px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <span
          css={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: tableHeader ? color.textIcon.secondary : color.black,
          }}
        >
          {tableHeader ? "ID" : shrinkStringInMiddle(participant?.insignia ?? "--", 13)}
        </span>
      </div>
    );
  };

  const NotificationIconButton = () => {
    return (
      <div
        css={{
          width: "20px",
          minWidth: "20px",
          display: canUpdateParticipant ? "flex" : "none",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {RIF(tableHeader, <img src={MessageGreyIcon} width="20" />)}
        {RIF(
          !tableHeader,
          participant?.activated ? (
            <NotificationButton
              enable={!!participant?.pushDeviceId}
              onClick={() => {
                sendMessage([participant]);
              }}
              data-testid="test_notificationButton"
              index={index as number}
            />
          ) : (
            <span css={{ color: color.grey_400 }}>--</span>
          ),
        )}
      </div>
    );
  };

  const LastSeenInfo = () => {
    return (
      <div
        css={{
          width: "136px",
          minWidth: "120px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {RIF(
          tableHeader,
          <div css={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span css={{ color: color.grey_600 }}>Last Seen</span>
            <Tooltip
              css={{ marginLeft: "8px" }}
              content="This denotes the most recent time that the participant's Labfront Companion app was connected to the internet."
            />
            {/* <button
              onClick={() => setLastSeenDescending?.((prev) => !prev)}
              css={{
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                width: '16px',
                height: '16px',
                backgroundImage: `url(${SortingIcon})`,
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                rotate: lastSeenDescending ? '0deg' : '180deg',
              }}
            /> */}
          </div>,
        )}
        {RIF(!tableHeader, LastSeenAndBatteryAlert(adherence?.status))}
      </div>
    );
  };
  const LastSeenAndBatteryAlert = (participantStatus: AdherenceStatus | undefined) => {
    const lastSeenString = participantActivated
      ? participantLastSeen
        ? (timeConvert({ time: participantLastSeen, type: TimeConvertType.lastSeen24 }) as string)
        : participantLastSync
          ? (timeConvert({ time: participantLastSync, type: TimeConvertType.lastSeen24 }) as string)
          : ""
      : "Not joined yet";

    const {
      deviceBatteryPercentage: batteryLevel,
      deviceBatteryPercentageTimestamp: batteryTimestamp,
    } = participantStatus || {};

    return (
      <div
        css={{
          color: participantActivated ? color.black : color.grey_400,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <span>{lastSeenString}</span>
        {batteryLevel ? (
          <BatteryAlert batteryLevel={batteryLevel} batteryTimestamp={batteryTimestamp} />
        ) : (
          <></>
        )}
      </div>
    );
  };

  const tagRef = useRef(null);
  const Tags = () => {
    return (
      <div
        ref={tagRef}
        css={{
          display: "flex",
          flex: 1,
          position: "relative",
        }}
      >
        {RIF(
          tableHeader,
          <div
            css={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span>Tag</span>
            <Tooltip content="You can attach tags to participants for grouping" />
          </div>,
        )}
        {RIF(
          !tableHeader && participant?.tagList?.length,
          <div
            css={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "4px",
            }}
          >
            {participant?.tagList?.map((tag) => (
              <Tag key={tag.id} tag={tag} />
            ))}
          </div>,
        )}
      </div>
    );
  };

  const RealtimeData = () => {
    return (
      <div
        css={{
          minWidth: "100px",
          maxWidth: "150px",
          display: "flex",
          alignItems: "center",
          justifyContent: "start",
        }}
      >
        {RIF(tableHeader, <span>Realtime Data</span>)}
        {RIF(!tableHeader, <span>{`${realtimeData}`}</span>)}
      </div>
    );
  };

  return (
    <div
      css={{
        display: "flex",
        alignContent: "center",
        gap: "16px",
        paddingLeft: "16px",
        backgroundColor: "transparent",
        borderBottom: `${tableHeader ? "2px" : "1px"} solid ${color.border.light}`,
        borderRight: `1px solid ${color.border.light}`,
        minHeight: tableHeader ? "56px" : rowHeight,
        position: tableHeader ? "sticky" : "relative",
        top: tableHeader ? 0 : "auto",
        // ':hover': {
        //   backgroundColor: 'rgba(156, 158, 174, 0.05)',
        // },
      }}
    >
      <RowIndex />
      {RIF(canUpdateParticipant, <CheckBox />)}
      <ParticipantInsignia />
      <NotificationIconButton />
      <LastSeenInfo />
      {RIF(displayRealtimeData, <RealtimeData />)}
      <Tags />
    </div>
  );
};
